// Step 5 of the client-side X12 pipeline (855 variant): walks one
// transaction's flat body segments (from step 2's envelope tree) into the
// loop structure a human reader thinks in — header, Name loops, PO1+ACK
// line-item-acknowledgment loops (with their acknowledgment-relevant
// sub-loop and nested Name sub-loop), and the summary. Only ever runs
// against a transaction whose ST01 is "855"; anything else is left alone so
// it can still be shown, read-only, by the UI. Mirrors parser850.ts's
// pattern one-for-one, but against the 855's own loop shape — PO-specific
// extras the 850 supports (PWK, IT8, CTB, TAX, PAM, LM/LQ, SLN, CSH,
// TC1/TC2, PCT, ISS, LDT, MSG) are deliberately not carried over, since the
// 855 doesn't support them. parser850.ts itself is never touched by this
// file.

import type {EdiSegment} from './tokenizer';
import type {EdiEnvelope, EdiGroup, EdiTransaction} from './envelope';

export interface Header855 {
  bak?: EdiSegment;
  cur?: EdiSegment;
  refs: EdiSegment[];
  per?: EdiSegment;
  fob?: EdiSegment;
  itd?: EdiSegment;
  dtm: EdiSegment[];
  td5?: EdiSegment;
  other: EdiSegment[];
}

export interface NameLoop855 {
  n1: EdiSegment;
  n2?: EdiSegment;
  n3?: EdiSegment;
  n4?: EdiSegment;
  n5?: EdiSegment;
  n6?: EdiSegment;
  n9: EdiSegment[];
  refs: EdiSegment[];
  per?: EdiSegment;
  other: EdiSegment[];
}

export interface ItemNameSubLoop855 {
  n1: EdiSegment;
  n2?: EdiSegment;
  n3?: EdiSegment;
  n4?: EdiSegment;
  other: EdiSegment[];
}

export interface ItemAckLoop855 {
  po1: EdiSegment;
  ack?: EdiSegment;
  pid: EdiSegment[];
  mea: EdiSegment[];
  pkg: EdiSegment[];
  po3?: EdiSegment;
  ctp?: EdiSegment;
  refs: EdiSegment[];
  per?: EdiSegment;
  sac: EdiSegment[];
  sdq: EdiSegment[];
  sch: EdiSegment[];
  dtm: EdiSegment[];
  nameSubLoops: ItemNameSubLoop855[];
  other: EdiSegment[];
}

export interface Summary855 {
  ctt?: EdiSegment;
  amt: EdiSegment[];
}

export interface Parsed855 {
  header: Header855;
  nameLoops: NameLoop855[];
  items: ItemAckLoop855[];
  summary: Summary855;
}

function attachHeaderSegment(header: Header855, s: EdiSegment): void {
  switch (s.id) {
    case 'BAK':
      header.bak = s;
      break;
    case 'CUR':
      header.cur = s;
      break;
    case 'REF':
      header.refs.push(s);
      break;
    case 'PER':
      header.per = s;
      break;
    case 'FOB':
      header.fob = s;
      break;
    case 'ITD':
      header.itd = s;
      break;
    case 'DTM':
      header.dtm.push(s);
      break;
    case 'TD5':
      header.td5 = s;
      break;
    default:
      header.other.push(s);
      break;
  }
}

function attachNameLoopSegment(loop: NameLoop855, s: EdiSegment): void {
  switch (s.id) {
    case 'N2':
      loop.n2 = s;
      break;
    case 'N3':
      loop.n3 = s;
      break;
    case 'N4':
      loop.n4 = s;
      break;
    case 'N5':
      loop.n5 = s;
      break;
    case 'N6':
      loop.n6 = s;
      break;
    case 'N9':
      loop.n9.push(s);
      break;
    case 'REF':
      loop.refs.push(s);
      break;
    case 'PER':
      loop.per = s;
      break;
    default:
      loop.other.push(s);
      break;
  }
}

function attachItemNameSubLoopSegment(
  loop: ItemNameSubLoop855,
  s: EdiSegment
): void {
  switch (s.id) {
    case 'N2':
      loop.n2 = s;
      break;
    case 'N3':
      loop.n3 = s;
      break;
    case 'N4':
      loop.n4 = s;
      break;
    default:
      loop.other.push(s);
      break;
  }
}

function attachItemSegment(item: ItemAckLoop855, s: EdiSegment): void {
  switch (s.id) {
    case 'ACK':
      item.ack = s;
      break;
    case 'PID':
      item.pid.push(s);
      break;
    case 'MEA':
      item.mea.push(s);
      break;
    case 'PKG':
      item.pkg.push(s);
      break;
    case 'PO3':
      item.po3 = s;
      break;
    case 'CTP':
      item.ctp = s;
      break;
    case 'REF':
      item.refs.push(s);
      break;
    case 'PER':
      item.per = s;
      break;
    case 'SAC':
      item.sac.push(s);
      break;
    case 'SDQ':
      item.sdq.push(s);
      break;
    case 'SCH':
      item.sch.push(s);
      break;
    case 'DTM':
      item.dtm.push(s);
      break;
    default:
      item.other.push(s);
      break;
  }
}

function emptyHeader(): Header855 {
  return {refs: [], dtm: [], other: []};
}

function emptyItem(po1: EdiSegment): ItemAckLoop855 {
  return {
    po1,
    pid: [],
    mea: [],
    pkg: [],
    refs: [],
    sac: [],
    sdq: [],
    sch: [],
    dtm: [],
    nameSubLoops: [],
    other: [],
  };
}

type Phase = 'header' | 'name' | 'item' | 'summary';
type ItemSubPhase = 'main' | 'name';

/**
 * Walks one 855 transaction's flat body segments (ST/SE excluded) into its
 * loop structure. Never throws — a segment that doesn't fit anywhere
 * sensible in the current loop is simply not attached, rather than the walk
 * aborting; the validator is what reports on a document that's missing or
 * misordered.
 */
export function parse855Segments(segments: EdiSegment[]): Parsed855 {
  const header = emptyHeader();
  const nameLoops: NameLoop855[] = [];
  const items: ItemAckLoop855[] = [];
  const summary: Summary855 = {amt: []};

  let phase: Phase = 'header';
  let itemSubPhase: ItemSubPhase = 'main';
  let currentNameLoop: NameLoop855 | undefined;
  let currentItem: ItemAckLoop855 | undefined;
  let currentItemNameSubLoop: ItemNameSubLoop855 | undefined;

  for (const s of segments) {
    switch (s.id) {
      case 'N1':
        if (phase === 'item' && currentItem) {
          itemSubPhase = 'name';
          currentItemNameSubLoop = {n1: s, other: []};
          currentItem.nameSubLoops.push(currentItemNameSubLoop);
        } else {
          phase = 'name';
          currentNameLoop = {n1: s, n9: [], refs: [], other: []};
          nameLoops.push(currentNameLoop);
        }
        break;

      case 'N2':
      case 'N3':
      case 'N4':
      case 'N5':
      case 'N6':
      case 'N9':
        if (itemSubPhase === 'name' && currentItemNameSubLoop) {
          attachItemNameSubLoopSegment(currentItemNameSubLoop, s);
        } else if (phase === 'name' && currentNameLoop) {
          attachNameLoopSegment(currentNameLoop, s);
        }
        break;

      case 'PO1':
        phase = 'item';
        itemSubPhase = 'main';
        currentItemNameSubLoop = undefined;
        currentItem = emptyItem(s);
        items.push(currentItem);
        break;

      case 'CTT':
        phase = 'summary';
        summary.ctt = s;
        currentItem = undefined;
        currentNameLoop = undefined;
        currentItemNameSubLoop = undefined;
        break;

      case 'AMT':
        summary.amt.push(s);
        break;

      default:
        if (phase === 'header') {
          attachHeaderSegment(header, s);
        } else if (phase === 'name' && currentNameLoop) {
          attachNameLoopSegment(currentNameLoop, s);
        } else if (phase === 'item' && currentItem) {
          attachItemSegment(currentItem, s);
        }
        break;
    }
  }

  return {header, nameLoops, items, summary};
}

export interface EdiTransactionWithParsed855 extends EdiTransaction {
  /** Left undefined for a transaction whose ST01 isn't "855" — it keeps only
   * its raw segment list, for read-only display. */
  parsed855?: Parsed855;
}

export interface EdiGroupWithParsed855 extends Omit<EdiGroup, 'transactions'> {
  transactions: EdiTransactionWithParsed855[];
}

export interface EdiEnvelopeWithParsed855 extends Omit<EdiEnvelope, 'groups'> {
  groups: EdiGroupWithParsed855[];
}

/**
 * Parses every 855 transaction in an envelope tree (step 2's output),
 * attaching `parsed855` to each. A non-855 transaction is passed through
 * unchanged, `parsed855` left undefined.
 */
export function attachParsed855(
  envelope: EdiEnvelope
): EdiEnvelopeWithParsed855 {
  return {
    ...envelope,
    groups: envelope.groups.map(group => ({
      ...group,
      transactions: group.transactions.map(transaction => ({
        ...transaction,
        parsed855:
          transaction.transactionSetCode === '855'
            ? parse855Segments(transaction.segments)
            : undefined,
      })),
    })),
  };
}
